import {PAGES_TEXTS} from "../pages/index.ts";



interface SearchResult {
  key: string;
  value: string;
}



const translationData = (page: string) => PAGES_TEXTS[page] || {};

/**
 * Search in the values of the English translations and return the key of the matching value
 * @param searchValue - The value to search for
 * @param exactMatch - If true, performs exact match; if false, performs case-insensitive partial match
 * @returns The key if found, null otherwise
 */
function findKeyByEnValue(
  searchValue: string, 
  exactMatch: boolean = false, 
  page: string
): string | null {
  const enTranslations = translationData(page).en as { [key: string]: string };

  if (exactMatch) {
    // Exact match search
    for (const [key, value] of Object.entries(enTranslations)) {
      if (value === searchValue) {
        return key;
      }
    }
  } else {
    // Case-insensitive partial match search
    const searchLower = searchValue.toLowerCase();
    for (const [key, value] of Object.entries(enTranslations)) {
      if (value.toLowerCase().includes(searchLower)) {
        return key;
      }
    }
  }

  return null;
}


/**
 * Search in the values of the English translations and return all matching keys
 * @param searchValue - The value to search for
 * @param exactMatch - If true, performs exact match; if false, performs case-insensitive partial match
 * @returns Array of search results with keys and values
 */
function findKeysByEnValue(
  searchValue: string, 
  exactMatch: boolean = false,
  page: string
): SearchResult[] {
  const enTranslations = translationData(page).en as { [key: string]: string };
  const results: SearchResult[] = [];

  if (exactMatch) {
    for (const [key, value] of Object.entries(enTranslations)) {
      if (value === searchValue) {
        results.push({ key, value });
      }
    }
  } else {
    const searchLower = searchValue.toLowerCase();
    for (const [key, value] of Object.entries(enTranslations)) {
      if (value.toLowerCase().includes(searchLower)) {
        results.push({ key, value });
      }
    }
  }

  return results;
}


/**
 * Get the key for a specific English value (with optional language parameter for cross-reference)
 * @param searchValue - The English value to search for
 * @param language - Optional language code to also return the translation in that language
 * @returns Object containing the key and optional translation in specified language
 */
function findTranslationKey(
  page:string,
  searchValue: string, 
  language?: string,
): { key: string | null; translation?: string } {
  const enTranslations = translationData(page).en;
  let foundKey: string | null = null;
  
  // Find the key in English
  for (const [key, value] of Object.entries(enTranslations)) {
    if (value === searchValue) {
      foundKey = key;
      break;
    }
  }
  
  if (!foundKey) {
    return { key: null };
  }
  
  // If language is specified, get the translation in that language
  if (language && translationData(page)[language]) {
    const translation = translationData(page)[language][foundKey];
    return { key: foundKey, translation };
  }
  
  return { key: foundKey };
}




/**
 * Get translation value for a given key in a specified language
 * @param key - The translation key to look up
 * @param language - The language code (default: "en")
 * @returns The translation value if found, null otherwise
 */
function getTranslationByKey(key: string, language: string = "en",page:string): string | null {
  // Check if the language exists in translation data
  if (!translationData(page)[language]) {
    // Handle error silently
    return null;
  }
  
  // Check if the key exists in the specified language
  if (key in translationData(page)[language]) {
    return translationData(page)[language][key];
  }
  
  // If not found in specified language, try English as fallback
  if (language !== "en" && key in translationData(page).en) {
    // Handle error silently
    return translationData(page).en[key];
  }
  
  return null;
}

/**
 * Get translations for a given key in all available languages
 * @param key - The translation key to look up
 * @returns Object with language codes as keys and translations as values
 */
function getTranslationsByKey(key: string,page:string): { [language: string]: string } {
  const translations: { [language: string]: string } = {};
  
  for (const language of Object.keys(translationData)) {
    if (key in translationData(page)[language]) {
      translations[language] = translationData(page)[language][key];
    }
  }
  
  return translations;
}

/**
 * Search for a key and return its value in the specified language
 * @param searchValue - The value to search for in English
 * @param language - The language code to return the value in (default: "en")
 * @param exactMatch - If true, performs exact match; if false, performs case-insensitive partial match
 * @returns The value in the specified language if key is found, null otherwise
 */
function getValueByEnKeySearch(
  page:string,
  searchValue: string, 
  language: string = "en",
  exactMatch: boolean = false
): string | null {
  // First, find the key using the existing search function
  const key = findKeyByEnValue(searchValue, exactMatch,page);
  
  if (!key) {
    return null;
  }
  
  // Then get the translation for that key in the specified language
  return getTranslationByKey(key, language,page);
}

/**
 * Comprehensive search function that returns both key and value
 * @param searchValue - The value to search for
 * @param targetLanguage - The language to return the value in (default: "en")
 * @param searchLanguage - The language to search in (default: "en")
 * @param exactMatch - If true, performs exact match; if false, performs case-insensitive partial match
 * @returns Object containing the key and value in target language
 */
function findKeyAndValue(
  page:string,
  searchValue: string,
  targetLanguage: string = "en",
  searchLanguage: string = "en",
  exactMatch: boolean = false
): SearchResult | null {
  // Validate that search language exists
  if (!translationData(page)[searchLanguage]) {
    console.warn(`Search language "${searchLanguage}" not found`);
    return null;
  }
  
  // Validate that target language exists (fallback to searchLanguage if not)
  const finalTargetLanguage = translationData(page)[targetLanguage] ? targetLanguage : searchLanguage;
  

  let foundKey: string | null = null;
  
  const searchTranslations = translationData(page)[searchLanguage] as { [key: string]: string };

if (exactMatch) {
  for (const [key, value] of Object.entries(searchTranslations)) {
    if (value === searchValue) {
      foundKey = key;
      break;
    }
  }
} else {
  const searchLower = searchValue.toLowerCase();
  for (const [key, value] of Object.entries(searchTranslations)) {
    if (value.toLowerCase().includes(searchLower)) {
      foundKey = key;
      break;
    }
  }
}

  
  if (!foundKey) {
    return null;
  }
  
  // Get the value in the target language
  const value = getTranslationByKey(foundKey, finalTargetLanguage,page);
  
  if (!value) {
    return null;
  }
  
  return { key: foundKey, value };
}

// Example usage:
// const value = getTranslationByKey("goodMorning", "fr"); // Returns French translation of "goodMorning"
// const allTranslations = getTranslationsByKey("goodMorning"); // Returns all language versions
// const searchResult = getValueByEnKeySearch("Good Morning", "fr"); // Returns French translation based on English search
// const keyAndValue = findKeyAndValue("Good Morning", "fr", "en", false); // Returns key and French value

export {
  getTranslationByKey,
  getTranslationsByKey,
  getValueByEnKeySearch,
  findKeyAndValue,
  findKeyByEnValue,
  findKeysByEnValue,
  findTranslationKey
};